import { useEffect } from "react";

const OFFICIAL_SITE = "https://ooobcs.org";

const PRIVACY_EMAIL = "bcsict@ooobcs.org";
const DELETION_MAILTO = `mailto:${PRIVACY_EMAIL}?subject=${encodeURIComponent(
  "BCS Hymnary account deletion request"
)}&body=${encodeURIComponent(
  "Please delete my BCS Hymnary account and the personal information associated with it.\n\nAccount email:\nMember name (if any):\n"
)}`;

const SECTIONS = [
  { id: "who-we-are", title: "1. Who we are" },
  { id: "scope", title: "2. What this policy covers" },
  { id: "summary", title: "3. Summary of personal information" },
  { id: "collect", title: "4. Information we collect" },
  { id: "use", title: "5. How we use information" },
  { id: "share", title: "6. How we share information" },
  { id: "not-collected", title: "7. Information we do not collect" },
  { id: "device", title: "8. Information stored on your device" },
  { id: "retention", title: "9. How long we keep information" },
  { id: "deletion", title: "10. Delete your account and data" },
  { id: "rights", title: "11. Your privacy rights" },
  { id: "children", title: "12. Children's privacy" },
  { id: "security", title: "13. Security" },
  { id: "international", title: "14. International users" },
  { id: "stores", title: "15. App stores and other services" },
  { id: "changes", title: "16. Changes to this policy" },
  { id: "contact", title: "17. Contact us" },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-32">
      <h2 className="text-xl md:text-2xl font-semibold text-[#16315c]">{title}</h2>
      <div className="mt-3 space-y-3 text-[15px] md:text-base leading-relaxed text-stone-700">
        {children}
      </div>
    </section>
  );
}

export default function HymnaryPrivacy() {
  useEffect(() => {
    const previous = document.title;
    document.title = "BCS Hymnary Privacy Policy";
    return () => {
      document.title = previous;
    };
  }, []);

  return (
    <main className="bg-[#f6f1ea] text-stone-800">
      <header className="px-5 md:px-10 pt-36 md:pt-44 pb-10">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col items-center text-center">
            <img
              src="/hymnary/app-icon.jpg"
              alt="BCS Hymn app icon"
              className="w-28 h-28 md:w-36 md:h-36 rounded-[28px] object-cover shadow-lg border border-white"
            />
            <p className="mt-6 text-[11px] font-bold tracking-[0.35em] uppercase text-[#8a6a2f]">
              Brotherhood of the Cross and Star
            </p>
            <h1 className="mt-3 text-4xl md:text-6xl font-black tracking-tight text-[#16315c]">
              BCS Hymnary
            </h1>
            <p className="mt-2 text-lg md:text-2xl font-semibold text-[#6e2433]">
              Privacy Policy
            </p>
            <p className="mt-4 max-w-2xl text-sm md:text-base text-stone-600">
              This is the public privacy policy for the BCS Hymnary mobile app
              on Google Play and the Apple App Store. It is published by the
              Brotherhood of the Cross and Star. The official website is{" "}
              <a href={OFFICIAL_SITE} className="underline text-[#16315c]">
                ooobcs.org
              </a>
              .
            </p>
            <p className="mt-3 text-sm text-stone-500">
              Effective date: 16 October 2026
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            <figure className="md:col-span-3 bg-white rounded-3xl p-3 shadow-sm border border-[#eadfce]">
              <img
                src="/hymnary/features.jpg"
                alt="BCS Hymnary features, including sign-in, hymn lyrics, search, and favorites"
                className="w-full rounded-2xl object-cover"
              />
            </figure>
            <figure className="bg-white rounded-3xl p-3 shadow-sm border border-[#eadfce]">
              <img
                src="/hymnary/sign-in.jpg"
                alt="BCS Hymnary sign-in screen with email and password fields"
                className="w-full rounded-2xl object-cover object-top max-h-[520px]"
              />
              <figcaption className="px-2 pt-3 pb-1 text-xs text-stone-500">
                Sign-in is optional and uses an email address and password.
              </figcaption>
            </figure>
            <figure className="md:col-span-2 bg-white rounded-3xl p-3 shadow-sm border border-[#eadfce]">
              <img
                src="/hymnary/home.jpg"
                alt="BCS Hymnary home screen showing the daily devotional and hymn categories"
                className="w-full h-full max-h-[520px] rounded-2xl object-cover object-top"
              />
              <figcaption className="px-2 pt-3 pb-1 text-xs text-stone-500">
                Hymns, devotionals, categories, search, and favorites.
              </figcaption>
            </figure>
          </div>
        </div>
      </header>

      <div className="px-5 md:px-10 pb-24">
        <div className="max-w-3xl mx-auto space-y-10">
          <aside className="rounded-3xl border border-[#6e2433]/20 bg-white p-6 md:p-8 shadow-sm">
            <h2 className="text-lg font-semibold text-[#6e2433]">
              Delete your account
            </h2>
            <p className="mt-2 text-sm md:text-base leading-relaxed text-stone-700">
              If you created a BCS Hymnary account, you can ask us to delete
              that account and the personal information stored with it. Send
              the request from the email address on the account, or include
              that address in the message. We complete verified requests within
              30 days.
            </p>
            <a
              href={DELETION_MAILTO}
              className="mt-4 inline-flex items-center justify-center rounded-full bg-[#6e2433] px-5 py-3 text-sm font-semibold text-white"
            >
              Request account deletion
            </a>
            <p className="mt-3 text-sm text-stone-500">
              Full steps are in{" "}
              <a href="#deletion" className="underline text-[#16315c]">
                section 10
              </a>
              .
            </p>
          </aside>

          <nav aria-label="Privacy policy contents" className="rounded-3xl bg-white border border-[#eadfce] p-6">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-stone-400">
              Contents
            </p>
            <ol className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {SECTIONS.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className="text-[#16315c] underline decoration-[#eadfce] underline-offset-2">
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <Section id="who-we-are" title="1. Who we are">
            <p>
              BCS Hymnary is a hymn app published by the Brotherhood of the
              Cross and Star (“we”, “us”). The app lets people read hymn
              lyrics, browse categories, open a daily devotional, search, and,
              if they choose, sign in to bookmark and favorite hymns.
            </p>
            <p>
              This policy is the privacy policy for that app. The official
              website of the Brotherhood of the Cross and Star is{" "}
              <a href={OFFICIAL_SITE} className="underline text-[#16315c]">
                {OFFICIAL_SITE}
              </a>
              . The public address of this policy is{" "}
              <span className="font-medium">{OFFICIAL_SITE}/hymnary-privacy</span>.
            </p>
          </Section>

          <Section id="scope" title="2. What this policy covers">
            <p>
              This policy applies to the BCS Hymnary app distributed on Google
              Play and the Apple App Store, and to account, bookmark, and
              support requests handled for that app. It does not change the
              privacy terms of Apple, Google, or any other service you use
              separately.
            </p>
            <p>
              You can use hymn text in the app without creating an account.
              An account is required only for features that are saved to you,
              such as bookmarks, favorites, and a verified-member profile.
            </p>
          </Section>

          <Section id="summary" title="3. Summary of personal information">
            <p>
              This summary matches the information the app is designed to
              handle. We do not sell personal information, and we do not use it
              for advertising or cross-app tracking.
            </p>
            <div className="overflow-x-auto rounded-2xl border border-[#eadfce]">
              <table className="w-full text-left text-sm">
                <thead className="bg-[#f6f1ea] text-[#16315c]">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Information</th>
                    <th className="px-3 py-2 font-semibold">When</th>
                    <th className="px-3 py-2 font-semibold">Why</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#eadfce]">
                  <tr>
                    <td className="px-3 py-2">Email address</td>
                    <td className="px-3 py-2">If you create an account or sign in</td>
                    <td className="px-3 py-2">Account access and support</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Password</td>
                    <td className="px-3 py-2">If you create an account</td>
                    <td className="px-3 py-2">Authentication only. It is not stored in plain text.</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Name shown on the account</td>
                    <td className="px-3 py-2">If you provide one</td>
                    <td className="px-3 py-2">Profile and member display</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Verified-member status</td>
                    <td className="px-3 py-2">If your account is verified</td>
                    <td className="px-3 py-2">Show that status in the app</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Bookmarks and favorites</td>
                    <td className="px-3 py-2">If you save them</td>
                    <td className="px-3 py-2">Restore the hymns you chose</td>
                  </tr>
                  <tr>
                    <td className="px-3 py-2">Search text</td>
                    <td className="px-3 py-2">When you search</td>
                    <td className="px-3 py-2">Find matching hymns</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Section>

          <Section id="collect" title="4. Information we collect">
            <p>Depending on how you use the app, we may process:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <span className="font-medium">Account details.</span> Email
                address, password, and the name associated with the account
                when you register or sign in.
              </li>
              <li>
                <span className="font-medium">App content you save.</span>{" "}
                Hymn bookmarks, favorites, and similar items you choose to keep
                on your account.
              </li>
              <li>
                <span className="font-medium">Search.</span> The words you type
                into hymn search, so the app can return matching hymns.
              </li>
              <li>
                <span className="font-medium">Support messages.</span> The
                contents of an email you send us, including a deletion or
                correction request, and the address you write from.
              </li>
            </ul>
            <p>
              Hymn lyrics, category names, and the daily devotional are content
              of the app. They are not personal information about you.
            </p>
          </Section>

          <Section id="use" title="5. How we use information">
            <p>We use personal information only to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>create and secure your account;</li>
              <li>sign you in and keep bookmarks and favorites available to you;</li>
              <li>show a verified-member status where that applies;</li>
              <li>answer a support, access, correction, or deletion request;</li>
              <li>protect the app, investigate misuse, and meet a legal duty.</li>
            </ul>
            <p>
              We do not use this information to sell advertising, build
              advertising profiles, or track you across other companies’ apps
              and websites.
            </p>
          </Section>

          <Section id="share" title="6. How we share information">
            <p>
              We do not sell personal information. We do not share it with data
              brokers. We do not share it for third-party advertising.
            </p>
            <p>We disclose personal information only in these cases:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <span className="font-medium">Service providers.</span> A
                hosting or sign-in provider may process account data strictly
                so the app can authenticate you and store bookmarks. Those
                providers may use the data only to perform that work for us.
              </li>
              <li>
                <span className="font-medium">App stores.</span> Apple and
                Google handle downloads of the app under their own privacy
                policies. We do not receive your App Store or Google Play
                payment details.
              </li>
              <li>
                <span className="font-medium">Legal reasons.</span> We may
                disclose information if the law, a court, or a competent
                authority requires it, or if disclosure is needed to protect
                someone from serious harm.
              </li>
            </ul>
          </Section>

          <Section id="not-collected" title="7. Information we do not collect">
            <p>BCS Hymnary does not ask for, and is not designed to collect:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>precise or approximate location;</li>
              <li>contacts, SMS, or call logs;</li>
              <li>photos, camera, or microphone recordings;</li>
              <li>financial or payment card information;</li>
              <li>advertising identifiers for ads or measurement;</li>
              <li>health, biometric, or government identity numbers.</li>
            </ul>
            <p>
              The app does not contain third-party advertising and does not
              participate in cross-app tracking.
            </p>
          </Section>

          <Section id="device" title="8. Information stored on your device">
            <p>
              Hymn text is stored on your device so worship can continue when
              the network is unavailable. Preferences needed to open the app
              may also remain on the device. That information stays on the
              device until you clear the app’s storage or uninstall BCS Hymnary.
              Uninstalling the app does not, by itself, delete an account that
              was stored on our servers. Use section 10 to delete the account.
            </p>
          </Section>

          <Section id="retention" title="9. How long we keep information">
            <p>
              Account details, bookmarks, favorites, and verified-member status
              are kept while the account is open. After we verify a deletion
              request, we delete that personal information within 30 days,
              unless a longer period is required by law.
            </p>
            <p>
              Support emails are kept only as long as needed to complete the
              request and keep a record that it was handled. Search text is
              used to return results and is not kept as a marketing profile.
            </p>
          </Section>

          <Section id="deletion" title="10. Delete your account and data">
            <p>
              You can request deletion of your BCS Hymnary account and the
              personal information associated with it at any time. You do not
              need to install anything else, and you do not need to give a
              reason.
            </p>
            <ol className="list-decimal pl-5 space-y-2">
              <li>
                Email{" "}
                <a href={DELETION_MAILTO} className="underline text-[#16315c]">
                  {PRIVACY_EMAIL}
                </a>{" "}
                with the subject “BCS Hymnary account deletion request”.
              </li>
              <li>
                Send the message from the email address on the account, or
                write that address in the message, together with the name on
                the account if there is one.
              </li>
              <li>
                We may send one reply to that address to confirm the request
                is yours.
              </li>
              <li>
                After confirmation, we delete the account email, name,
                password credential, verified-member status, bookmarks, and
                favorites stored for that account.
              </li>
            </ol>
            <p>
              Deletion is completed within 30 days of confirmation. We may
              retain a minimal record of the request itself where the law
              requires it. Copies on your own device are removed when you
              clear the app’s data or uninstall it. This page, on the official
              website, is the web address for account and data deletion
              requests:{" "}
              <span className="font-medium">{OFFICIAL_SITE}/hymnary-privacy</span>.
            </p>
          </Section>

          <Section id="rights" title="11. Your privacy rights">
            <p>You may ask us to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>confirm whether we hold personal information about you;</li>
              <li>provide a copy of the account information we hold;</li>
              <li>correct a name or email address that is wrong;</li>
              <li>delete the account and associated information, as described above;</li>
              <li>stop a use of your information that is not required to provide the app.</li>
            </ul>
            <p>
              Send the request to {PRIVACY_EMAIL}. If a privacy law where you
              live gives you additional rights, including rights under the
              GDPR, UK GDPR, or the California Consumer Privacy Act, you may
              exercise them through the same address. We will not discriminate
              against you for making a request. We do not sell or share
              personal information as those laws define “sale” or “sharing”
              for advertising.
            </p>
          </Section>

          <Section id="children" title="12. Children's privacy">
            <p>
              BCS Hymnary is a general-audience worship app. It is not directed
              to children under 13, and we do not knowingly collect personal
              information from children under 13. An account should be created
              only by a person who is old enough to consent under the law that
              applies to them, or by a parent or guardian.
            </p>
            <p>
              If you believe a child under 13 has given us personal
              information, email {PRIVACY_EMAIL}. We will delete that
              information.
            </p>
          </Section>

          <Section id="security" title="13. Security">
            <p>
              Account passwords are processed for authentication and are not
              kept in plain text. Account traffic is sent over an encrypted
              connection. Access to account records is limited to people who
              operate the app and need the information for the purposes in this
              policy. No method of storage or transmission is perfectly secure,
              and we cannot guarantee absolute security.
            </p>
          </Section>

          <Section id="international" title="14. International users">
            <p>
              The Brotherhood of the Cross and Star operates BCS Hymnary for
              members and other users in more than one country. If you use the
              app from outside the country where the account is hosted, your
              information may be processed in that hosting country. We apply
              the protections in this policy wherever the information is
              handled for the app.
            </p>
          </Section>

          <Section id="stores" title="15. App stores and other services">
            <p>
              When you download BCS Hymnary, Apple or Google may collect
              information under their own policies, such as the account you use
              on that store. Those practices are not controlled by this policy.
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>
                <a
                  href="https://www.apple.com/legal/privacy/"
                  className="underline text-[#16315c]"
                  target="_blank"
                  rel="noreferrer"
                >
                  Apple Privacy Policy
                </a>
              </li>
              <li>
                <a
                  href="https://policies.google.com/privacy"
                  className="underline text-[#16315c]"
                  target="_blank"
                  rel="noreferrer"
                >
                  Google Privacy Policy
                </a>
              </li>
            </ul>
          </Section>

          <Section id="changes" title="16. Changes to this policy">
            <p>
              If this policy changes, the updated version will be posted on
              this page with a new effective date. Where a change materially
              affects an existing account, we will provide notice in the app
              or by email when we have an address for the account. Continued
              use of BCS Hymnary after the effective date means the updated
              policy applies.
            </p>
          </Section>

          <Section id="contact" title="17. Contact us">
            <p>
              Privacy, access, correction, and deletion requests for BCS
              Hymnary:
            </p>
            <p>
              Brotherhood of the Cross and Star
              <br />
              BCS Hymnary
              <br />
              Email:{" "}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="underline text-[#16315c]">
                {PRIVACY_EMAIL}
              </a>
              <br />
              Official website:{" "}
              <a
                href={OFFICIAL_SITE}
                className="underline text-[#16315c]"
              >
                {OFFICIAL_SITE}
              </a>
            </p>
          </Section>
        </div>
      </div>
    </main>
  );
}
